import { useFormContext } from "#src/contexts/FormSchemaContext";
import {
  useMemoizedFormState,
  useMultiStepFormContext,
} from "#src/hooks/useMultiStepForm";
import { useQuery } from "@tanstack/react-query";
import {
  CheckboxInput,
  DropdownInput,
  Form,
  Panel,
  RadioInput,
  TextAreaInput,
  TextInput,
  useForm,
} from "@validereinc/common-components";
import { FormCategoryAdapter, RolesAdapter } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useEffect } from "react";
import { useParams } from "react-router";
import styles from "./CreateFormTemplateDetailsStep.module.css";

const cx = classNames.bind(styles);

const STEP_NUMBER = 1;

export const CreateFormTemplateDetailsStep = () => {
  const {
    schema: { data: templateToEdit },
  } = useFormContext();

  const { categoryId } = useParams<{
    categoryId: string;
  }>();

  const categoriesQuery = useQuery({
    queryKey: ["formCategories"],
    queryFn: () => FormCategoryAdapter.getList({}),
    enabled: typeof templateToEdit !== "undefined",
  });

  const { currentStep, updateStep } = useMultiStepFormContext();

  const form = useForm({
    defaultValues: {
      name: templateToEdit?.name ?? "",
      description: templateToEdit?.description ?? "",
      status: templateToEdit?.status ?? "active",
      form_submission_default_status:
        templateToEdit?.form_submission_default_status ?? "pending",
      form_submission_editable:
        templateToEdit?.form_submission_editable ?? true,
      form_category_id: templateToEdit?.form_category_id ?? categoryId,
      form_submission_restricted_role_ids:
        templateToEdit?.form_submission_restricted_role_ids ?? [],
      should_restrict_form_submissions:
        Boolean(templateToEdit?.form_submission_restricted_role_ids?.length) ??
        false,
    },
  });

  const getFormState = useMemoizedFormState(form);
  const shouldRestrictFormSubmissions = form.watch(
    "should_restrict_form_submissions"
  );

  useEffect(() => {
    updateStep(STEP_NUMBER, {
      getValues: () => form.getValues(),
      getFormState,
    });
  }, [getFormState]);

  return (
    <>
      {currentStep === STEP_NUMBER ? (
        <Panel title="Template Details">
          <Form {...form}>
            <div className={cx("inputs-container")}>
              <TextInput
                label="Name"
                name="name"
                isRequired
                rules={{
                  validate: {
                    maxLength: (name) =>
                      name.length < 100 ||
                      "Name must be less than 100 characters.",
                  },
                }}
              />

              {!!templateToEdit && (
                <DropdownInput
                  label="Form Category"
                  name="form_category_id"
                  options={
                    categoriesQuery.data?.data?.map((category) => ({
                      label: category.name,
                      value: category.id,
                    })) ?? []
                  }
                  labelKey="label"
                  valueKey="value"
                  isRequired
                  isLoading={categoriesQuery.isLoading}
                  isBusy={categoriesQuery.isFetching}
                />
              )}
              <TextAreaInput
                label="Description"
                name="description"
                rules={{
                  validate: {
                    maxLength: (description) =>
                      description.length < 1024 ||
                      "Description must be less than 1024 characters.",
                  },
                }}
              />

              <DropdownInput
                label="Default Form Submission Status"
                name="form_submission_default_status"
                options={[
                  { label: "Validated", value: "validated" },
                  { label: "Invalidated", value: "invalidated" },
                  { label: "Pending", value: "pending" },
                  { label: "Submitted", value: "submitted" },
                ]}
                labelKey="label"
                valueKey="value"
                isRequired
              />

              <RadioInput
                label="Edit Access"
                name="form_submission_editable"
                options={[
                  { label: "Submissions can be edited", value: true },
                  { label: "Submissions cannot be edited", value: false },
                ]}
                labelKey="label"
                valueKey="value"
                isRequired
              />

              <CheckboxInput
                label="Restrict view and edit access to submission author"
                name="should_restrict_form_submissions"
                isRequired
                isLabelShown={false}
              />
              {shouldRestrictFormSubmissions ? (
                <div className={cx("indented")}>
                  <div>
                    <p>
                      Override submission access by role. Select the role(s)
                      that can view submissions from all users for this template
                    </p>
                  </div>
                  <DropdownInput
                    name={"form_submission_restricted_role_ids"}
                    description="Select the role(s) that can view submissions from all users for this template"
                    onFetchData={async (payload) => {
                      let { data } = await RolesAdapter.getList({
                        ...payload,
                        pageSize: 500,
                        filters: {
                          ...(payload.searchTerm
                            ? {
                                name: payload.searchTerm,
                              }
                            : {}),
                        },
                      });

                      // REVIEW: not a fan of the fact that we need to do this. A refactor of DropdownInput is needed.
                      if (Array.isArray(payload.value)) {
                        data = data.filter((d) => payload.value.includes(d.id));
                      }

                      return data;
                    }}
                    labelKey="name"
                    valueKey="id"
                    placeholder="Select Roles..."
                    label=""
                    isInline
                    isMulti
                    isClearable
                    isLabelShown={false}
                  />
                </div>
              ) : null}
            </div>
          </Form>
        </Panel>
      ) : null}
    </>
  );
};
